import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { useDispatch, useSelector } from "react-redux";
import { 
  closeProviderDialog, 
  addProviderToCart, 
  removeProviderFromCart,
  resetServiceBooking,
  closeDialogForLogin,
  setShouldProcessCartAfterLogin,
  openServiceDialog
} from "../../../store/serviceBookingSlice";
import { useNavigate } from "react-router-dom";
import styles from './ProviderSelectionDialog.module.css';
import PriestProviderCard from './PriestProviderCard';
import CateringProviderCard from'./CateringProviderCard';
import TentProviderCard from './TentProviderCard';

import {   
  getPriestsListByLocation,
  getTentHouseListByLocationpost,
  getCateringListByLocation,
  getCateringItemMasterList 
} from "../../../Apiservices/Auth/servers/fetchProviderData";

// Import Venue related
import { createEvent } from "../../../Apiservices/Auth/servers/createEvents";

const ProviderSelectionDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { 
    isProviderDialogOpen, 
    serviceType, 
    selectedProviders, 
    commonData,
    priestEventName 
  } = useSelector(state => state.serviceBooking);
  
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);

  useEffect(() => {
    if (isProviderDialogOpen && serviceType) {
      localStorage.setItem('inProviderDialog', 'true');
      fetchProviders();
    } else {
      localStorage.removeItem('inProviderDialog');
    }
  }, [isProviderDialogOpen, serviceType]);

  // Simple back function in ProviderSelectionDialog
  const handleBackToSelection = () => {
    dispatch(closeProviderDialog());
    const pendingServiceData = {
      commonData,
      serviceType: serviceType,
      currentStep: serviceType === 'catering' ? 'cateringSelection' : 'tentItems'
    };
    localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
    setTimeout(() => {
      dispatch(openServiceDialog());
    }, 50);
  };

  const getBackButtonLabel = () => {
    switch (serviceType) {
      case 'tent': return 'Back to Tent Items';
      case 'catering': return 'Back to Menu Selection';
      default: return 'Back';
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      const { latitude, longitude, eventId, tentServiceIds } = commonData;

      switch (serviceType) {
        case 'catering':
          const cateringData = commonData.cateringData;
          const mealIds = cateringData?.meal ? [cateringData.meal.meal_id] : [];
          const allItemIds = [
            ...(cateringData?.included_items || []).map(item => item.itemId || item.id),
            ...(cateringData?.included_side_items || []).map(item => item.itemId || item.id)
          ].filter(Boolean);
          
          const cuisineTypeIds = commonData.cateringCuisineType 
            ? [Number(commonData.cateringCuisineType)] 
            : [];
            
          response = await getCateringListByLocation(latitude, longitude, eventId, {
            mealIds: mealIds,
            items: allItemIds,
            quantity: commonData.cateringQuantity || 10,
            cuisineMasterIds: cuisineTypeIds,
            cuisineIds: cuisineTypeIds,
            typeIds: cuisineTypeIds
          });
          break;                  
        case 'tent':
          response = await getTentHouseListByLocationpost(
            latitude, 
            longitude, 
            eventId,
            tentServiceIds || []
          );
          break;
        case 'priest':
          response = await getPriestsListByLocation(latitude, longitude, eventId);
          break;
        default:
          throw new Error('Invalid service type');
      }
      
      const transformedProviders = transformProviderData(response, serviceType);
      setProviders(transformedProviders);
      
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load providers. Please try again.');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (provider) => {
    if (!isLoggedIn) {
      const pendingServiceData = {
        commonData,
        serviceType,
        currentStep: 'providerSelection',
        attemptedProvider: provider,
        selectedProviders
      };
      localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
      
      dispatch(closeDialogForLogin());
      navigate("/login");
      return;
    }

    const isPriestInCart = selectedProviders.some(p => p.serviceType === 'priest');
    if (serviceType === 'priest' && isPriestInCart) {
      alert("Only one priest can be added per event. Please remove the existing priest first.");
      return;
    }
    
    const isSameTentVendorInCart = selectedProviders.some(
      p => p.serviceType === 'tent' && p.id === provider.id
    );
    
    if (serviceType === 'tent' && isSameTentVendorInCart) {
      alert("This tent provider is already in your cart. You can only select one tent provider per event.");
      return;
    }

    let enhancedSelectedTentItems = [];
    if (serviceType === 'tent' && commonData.selectedTentItems) {
      enhancedSelectedTentItems = commonData.selectedTentItems.map(item => {
        const service = provider.services?.find(s => s.serviceId === item.id);
        return {
          ...item,
          mapping_id: service?.mappingId
        };
      });
    }
    
    const cartItem = {
      ...provider,
      serviceType,
      eventDate: commonData.eventDate,
      location: commonData.address,
      latitude: commonData.latitude,
      longitude: commonData.longitude,
      whoseEvent: commonData.whoseEvent,
      ...(serviceType === 'tent' && { 
        selectedItems: enhancedSelectedTentItems,
        selectedTentItems: enhancedSelectedTentItems,
        tentServiceIds: commonData.tentServiceIds
      }),
      ...(serviceType === 'priest' && { eventName: priestEventName })
    };
    
    dispatch(addProviderToCart(cartItem));
  };

  const transformProviderData = (apiResponse, serviceType) => {
    if (!apiResponse || !apiResponse.data) return [];
    
    return apiResponse.data.map(provider => {
      const baseProvider = {
        id: provider.providerId || provider.id,
        originalData: provider,
        quantity: 1
      };

      switch (serviceType) {
        case 'priest':
          return {
            ...baseProvider,
            name: `${provider.firstName} ${provider.lastName || ''}`.trim(),
            firstName: provider.firstName,
            lastName: provider.lastName,
            rating: provider.rating || 0,
            price: provider.price || 2000,
            image: provider.profileImage,
            description: provider.description || `Experienced priest specializing in ${provider.events?.[0]?.name || 'various'} ceremonies`,
            distanceInMeter: provider.distanceInMeter,
            locality: provider.locality,
            city: provider.city,
            state: provider.state,
            experience: provider.experience,
            languages: provider.languages,
            events: provider.events
          };

        case 'catering':
          return {
            ...baseProvider,
            name: provider.businessName || provider.name || provider.providerName,
            rating: provider.rating || provider.averageRating || 0,
            price: formatPrice(provider.price, provider.rate, serviceType),
            image: provider.imageUrl,
            description: provider.description || provider.serviceDescription || 'Professional catering service',
            minOrder: provider.minOrder,
            capacity: provider.capacity,
            cuisineType: provider.cuisineType
          };

        case 'tent':
          const firstService = provider.services?.[0];
          return {
            ...baseProvider,
            name: provider.name || provider.businessName || 'Tent House Provider',
            rating: provider.rating || 0,
            price: firstService?.price || provider.price || 5000,
            image: provider.imageUrl,
            description: firstService?.description || provider.description || 'Professional tent house service',
            distanceInMeter: provider.distanceInMeter,
            locality: provider.locality,
            city: provider.city,
            state: provider.state,
            formattedAddress: provider.formattedAddress,
            serviceName: firstService?.serviceName,
            serviceDescription: firstService?.description,
            discountPrice: firstService?.discountPrice,
            services: provider.services,
            tentSize: provider.tentSize,
            capacity: provider.capacity,
            amenities: provider.amenities
          };

        default:
          return {
            ...baseProvider,
            name: provider.businessName || provider.name || provider.providerName || 'Service Provider',
            rating: provider.rating || provider.averageRating || 0,
            price: formatPrice(provider.price, provider.rate, serviceType),
            image: provider.imageUrl || provider.profileImage,
            description: provider.description || provider.serviceDescription || 'Professional service provider'
          };
      }
    });
  };

  const formatPrice = (price, rate, serviceType) => {
    if (price) return `₹${price}`;
    if (rate) return `₹${rate}`;
    
    switch (serviceType) {
      case 'catering': return '₹150 per plate';
      case 'tent': return '₹5000 per day';
      case 'priest': return '₹2000 per ceremony';
      default: return 'Price on request';
    }
  };

  const handleRemoveFromCart = (providerId) => {
    dispatch(removeProviderFromCart(providerId));
  };

  const handleQuantityChange = (providerId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setProviders(prev => prev.map(provider => 
      provider.id === providerId 
        ? { ...provider, quantity: newQuantity }
        : provider
    ));

    const providerInCart = selectedProviders.find(p => p.id === providerId);
    if (providerInCart) {
      dispatch(removeProviderFromCart(providerId));
      dispatch(addProviderToCart({
        ...providerInCart,
        quantity: newQuantity
      }));
    }
  };

  const isProviderInCart = (providerId) => {
    return selectedProviders.some(provider => provider.id === providerId);
  };

  const getProviderQuantity = (providerId) => {
    const providerInCart = selectedProviders.find(p => p.id === providerId);
    return providerInCart?.quantity || 1;
  };

  // ✅ Create Event function with proper data return
  const createEventForBooking = async () => {
    setIsCreatingEvent(true);
    try {
      const eventBody = {
        eventTitle: commonData.whoseEvent || "Event",
        event: {
          event: commonData.eventName || commonData.eventType || "Event",
          id: commonData.eventId || null
        },
        eventDesc: commonData.whoseEvent || "",
        Date: commonData.eventDate,
        EndDate: commonData.eventDate,
        Address: commonData.address,
        area: commonData.area,
        city: commonData.city,
        state: commonData.state,
        country: commonData.country,
        locationLat: commonData.latitude,
        locationLong: commonData.longitude
      };

      console.log("Creating event from ProviderSelectionDialog:", eventBody);
      const response = await createEvent(eventBody);
      console.log("Event created successfully:", response);
      
      // Store event data in localStorage for persistence
      if (response && response.id) {
        localStorage.setItem('currentEventId', response.id);
        localStorage.setItem('currentEventData', JSON.stringify({
          ...eventBody,
          id: response.id
        }));
      }
      
      return response;
    } catch (error) {
      console.error("Error creating event:", error);
      throw error;
    } finally {
      setIsCreatingEvent(false);
    }
  };

  // ✅ Updated handleProceedToLogin with proper event data passing
  const handleProceedToLogin = async () => {
    // Check if user is already logged in
    if (isLoggedIn) {
      // For venue, we need to create event and navigate
      if (serviceType === 'venue') {
        try {
          // Create event first
          const eventResponse = await createEventForBooking();
          
          // Get venue and space IDs
          const venueId = selectedProviders[0]?.id || commonData.selectedVenueId;
          const spaceId = commonData.selectedSpaceId;
          
          console.log("Venue booking data:", { venueId, spaceId, eventResponse });
          
          console.log("Navigating to venue booking with eventResponse 2:", eventResponse);


          if (venueId) {
            // Prepare complete event details
            const eventDetails = {
              eventId: commonData.eventId || eventResponse?.event?.id,
              eventTitle: commonData.eventName || commonData.eventType || commonData.whoseEvent || "Event",
              eventDescription: commonData.whoseEvent || "",
              Date: commonData.eventDate,
              locationLat: commonData.latitude,
              locationLong: commonData.longitude,
              address: commonData.address,
              area: commonData.area,
              city: commonData.city,
              state: commonData.state,
              country: commonData.country,
              venueId: venueId,
              spaceId: spaceId,
            };
            
            console.log("Navigating to venue booking with event details:", eventDetails);
            
            // Navigate with comprehensive data
            navigate(`/venue-booking/${venueId}`, {
              state: {
                venueId: venueId,
                spaceId: spaceId,
                eventId: eventResponse?.id || commonData.eventId,
                eventData: eventResponse,
                eventDetails: eventDetails,
                bookingData: {
                  eventId: eventResponse?.id,
                  venueId: venueId,
                  spaceId: spaceId,
                  eventDate: commonData.eventDate,
                  eventTime: commonData.eventTime,
                  address: commonData.address,
                  latitude: commonData.latitude,
                  longitude: commonData.longitude,
                  whoseEvent: commonData.whoseEvent,
                }
              }
            });
            
            // Close the dialog
            dispatch(closeDialogForLogin());
            dispatch(closeProviderDialog());
          } else {
            alert("Venue information is missing. Please try again.");
          }
        } catch (error) {
          console.error("Error creating event for venue:", error);
          alert("Failed to create event. Please try again.");
        }
        return;
      }

      // For other services, process cart
      if (selectedProviders.length > 0) {
        dispatch(setShouldProcessCartAfterLogin(true));
        localStorage.setItem('showEventTabsAfterLogin', 'true');
        
        const pendingCartData = {
          commonData,
          selectedProviders,
          serviceType
        };
        localStorage.setItem('pendingCartData', JSON.stringify(pendingCartData));
      }
      
      dispatch(closeDialogForLogin());
      dispatch(closeProviderDialog());
      
    } else {
      // User is not logged in - redirect to login
      if (selectedProviders.length > 0) {
        localStorage.setItem('showEventTabsAfterLogin', 'true');
      }
      
      const pendingServiceData = {
        commonData,
        serviceType,
        currentStep: 'providerSelection',
        selectedProviders
      };
      localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
      
      dispatch(closeDialogForLogin());
      navigate("/login");
    }
  };

  const handleClose = () => {
    dispatch(setShouldProcessCartAfterLogin(false));
    dispatch(closeProviderDialog());
    dispatch(resetServiceBooking());
  };

  const getDialogHeader = () => {
    switch (serviceType) {
      case 'catering': return 'Select Catering Service - Step 2 of 2';
      case 'tent': return 'Select Tent House - Step 2 of 2';
      case 'priest': return 'Select Priest - Step 2 of 2';
      case 'venue': return 'Select Venue - Step 2 of 2';
      default: return 'Select Provider';
    }
  };

  const getItemMatchType = (item) => {
    const userSelectedItems = commonData.cateringSelectedItems?.map(item => item.name) || [];
    
    if (!userSelectedItems.length) return 'other';
    
    const itemIncludedItems = item.includedItems || [];
    const itemAllText = [
      item.itemName,
      item.meal?.name,
      item.type?.name,
      item.category?.name,
      ...itemIncludedItems.map(i => i.itemName || i.name)
    ].filter(Boolean).join(' ').toLowerCase();
    
    const userSelectedText = userSelectedItems.join(' ').toLowerCase();
    
    const hasAllSelectedItems = userSelectedItems.every(selectedItem => 
      itemAllText.includes(selectedItem.toLowerCase())
    );
    
    if (hasAllSelectedItems) return 'exact';
    
    const hasSomeSelectedItems = userSelectedItems.some(selectedItem => 
      itemAllText.includes(selectedItem.toLowerCase())
    );
    
    if (hasSomeSelectedItems) return 'partial';
    
    return 'other';
  };

  const footerContent = (
    <div className={styles.footer}>
      <div className={styles.footerButtons}>
        {(serviceType === 'priest' || serviceType === 'tent' || serviceType === 'catering') && (
          <Button
            label={getBackButtonLabel()}
            icon="pi pi-arrow-left"
            className={styles.backButton}
            onClick={handleBackToSelection}
          />
        )}
        <Button
          label={isLoggedIn ? "Add to Cart" : "Proceed to Login"}
          icon={isLoggedIn ? "pi pi-shopping-cart" : "pi pi-user"}
          className={styles.loginButton}
          onClick={handleProceedToLogin}
          disabled={selectedProviders.length === 0 || isCreatingEvent}
        />
      </div>
    </div>
  );

  const getCateringSelectionInfo = () => {
    if (serviceType !== 'catering' || !commonData.cateringData) return null;
    
    const cateringData = commonData.cateringData;
    
    return (
      <div className={styles.cateringSelectionInfo}>
        <h4>Your Menu Selection:</h4>
        <div className={styles.cateringDetails}>
          <div className={styles.singlerow}>
            <p><strong>Package:</strong> {cateringData.item_name || cateringData.custom_combo_name}</p>
            {cateringData.meal?.meal_name && (
              <p><strong>Meal:</strong> {cateringData.meal.meal_name}</p>
            )}
          </div>
          {cateringData.included_items && cateringData.included_items.length > 0 && (
            <div className={styles.includedItems}>
              <strong>Included Items:</strong>
              <div className={styles.itemsList}>
                {cateringData.included_items.map((item, index) => (
                  <span key={index} className={styles.item}>
                    {item.itemName || ""}
                    {index < cateringData.included_items.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(cateringData.included_side_items && cateringData.included_side_items.length > 0) && (
            <div className={styles.sideItems}>
              <strong>Side Items:</strong>
              <div className={styles.itemsList}>
                {cateringData.included_side_items.map((item, index) => (
                  <span key={index} className={styles.item}>
                    {item.itemName || ""}
                    {index < cateringData.included_side_items.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProviderCard = (provider) => {
    const commonProps = {
      provider,
      isInCart: isProviderInCart(provider.id),
      onAddToCart: handleAddToCart,
      onRemoveFromCart: handleRemoveFromCart,
      serviceType,
      isLoggedIn,
    };

    switch (serviceType) {
      case 'priest':
        return (
          <PriestProviderCard
            key={provider.id}
            {...commonProps}
          />
        );
      case 'catering':
        return (
          <>
            {provider.originalData?.items?.map((item, index) => (
              <CateringProviderCard
                key={`${provider.id}-${item.itemId || index}`}
                provider={provider}
                item={item}
                isInCart={isProviderInCart(provider.id)}
                onAddToCart={handleAddToCart}
                onRemoveFromCart={handleRemoveFromCart}
                userSelectedItems={commonData.cateringSelectedItems?.map(item => item.name) || []}
                quantity={getProviderQuantity(provider.id)}
                onQuantityChange={handleQuantityChange}
                matchType={getItemMatchType(item)}
                isLoggedIn={isLoggedIn}
              />
            ))}
          </>
        );
      case 'tent':
        return (
          <TentProviderCard
            key={provider.id}
            {...commonProps}
            quantity={getProviderQuantity(provider.id)}
            onQuantityChange={handleQuantityChange}
            isLoggedIn={isLoggedIn} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      header={getDialogHeader()}
      visible={isProviderDialogOpen}
      onHide={handleClose}
      closable={true}
      breakpoints={{ "960px": "75vw", "640px": "100vw" }}
      className={styles.providerDialog}
      footer={footerContent}
    >
      <div className={styles.providerContainer}>
        <div className={styles.eventInfo}>
          <div className={styles.commondatainfo}>
            <p><strong>Venue:</strong> {commonData.address}</p>
            <p><strong>Date:</strong> {commonData.eventDate ? new Date(commonData.eventDate).toLocaleString() : 'Not set'}</p>
            <p><strong>For:</strong> {commonData.whoseEvent}</p>
          </div>
          {serviceType === 'catering' && getCateringSelectionInfo()}
          {serviceType === 'priest' && priestEventName && (
            <p><strong>Event:</strong> {priestEventName}</p>
          )}
          {serviceType === 'tent' && commonData.selectedTentItems && commonData.selectedTentItems.length > 0 && (
            <div className={styles.selectedItemsContainer}>
              <span>Selected Items</span>
              <div className={styles.selectedItemsScroll}>
                {commonData.selectedTentItems.map((item, index) => (
                  <span key={item.id} className={styles.selectedItem}>
                    {item.service_name}
                    {item.count > 1 && ` (${item.count})`}
                    {index < commonData.selectedTentItems.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
            <p>Loading providers...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>{error}</p>
            <Button
              label="Try Again"
              icon="pi pi-refresh"
              onClick={fetchProviders}
            />
          </div>
        )}

        {!loading && !error && (
          <div className={styles.providersScrollContainer}>
            <div className={styles.providersGrid}>
              {providers.map(provider => renderProviderCard(provider))}
            </div>
          </div>
        )}
        {providers.length === 0 && !loading && !error && serviceType !== "priest" && (
          <div className={styles.noProviders}>
            <p>No providers available for {serviceType} service in your area.</p>
            <Button
              label="Try Different Location"
              icon="pi pi-map-marker"
              onClick={handleBackToSelection}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ProviderSelectionDialog;